import { Fragment, useContext, useEffect, useState } from "react";
import { Context } from "../../context";
import axios from "axios";
import { getCurrentUser } from "../../services/login";
import { getCurrentFieldIdFromNew } from "../../workspace";
import {
    Button,
    DataGrid,
    DataGridBody,
    DataGridCell,
    DataGridHeader,
    DataGridHeaderCell,
    DataGridRow,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    ProgressBar,
    Field,
    DialogTrigger,
    Divider,
    Dropdown,
    Input,
    Label,
    Link,
    Option,
    Radio,
    RadioGroup,
    createTableColumn,
    makeStyles,
    shorthands,
    tokens,
} from "@fluentui/react-components";
import {
    cvtLeafPart,
    cvtRawDataStatus,
    cvtTaskStatus,
    idRecord,
    idString,
    parseDateAsPath,
} from "../../db_utils";
import {
    MoreCircleRegular,
    Dismiss24Regular,
    FolderRegular,
    LinkRegular,
    ArrowDownloadFilled,
    DeleteRegular,
    ArrowUpload24Filled,
    ArrowUploadRegular,
    NextFilled,
    EyeRegular,
} from "@fluentui/react-icons";
import {
    DrawerOverlay,
    DrawerBody,
    DrawerHeader,
    DrawerHeaderTitle,
    Drawer,
    DrawerProps,
} from "@fluentui/react-components/unstable";
import { TableDef } from "../../table_def";
import { SimpleGrid } from "../../components/simple_grid";

const useStyles = makeStyles({
    root: {
        height: "100%",
        overflowY: "auto",
        display: "grid",
        gridTemplateColumns: "2fr 7fr",
        ...shorthands.flex(1),
        ...shorthands.padding("10px"),
        ...shorthands.gap("10px"),
    },
    list: {
        display: "flex",
        overflowY: "auto",
        flexDirection: "column",
        backgroundColor: "#eee",
        ...shorthands.flex(1),
        ...shorthands.padding("10px"),
        ...shorthands.gap("10px"),
    },
    upload: {
        display: "flex",
        flexDirection: "column",
        overflowY: "auto",
        ...shorthands.flex(4),
        ...shorthands.padding("10px"),
        ...shorthands.gap("10px"),
    },
    card: {
        display: "flex",
        backgroundColor: "white",
        flexDirection: "column",
        boxShadow: tokens.shadow4,
        ...shorthands.padding("10px"),
        ...shorthands.gap("5px"),
        ...shorthands.margin("10px"),
    },
    turbineUpload: {
        display: "flex",
        flexDirection: "column",
        backgroundColor: "white",
        ...shorthands.padding("10px"),
        ...shorthands.gap("5px"),
        ...shorthands.margin("10px"),
    },
    leafUpload: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "start",
        ...shorthands.gap("10px"),
        ...shorthands.padding("5px"),
        ...shorthands.flex(1),
    },
});

const isRootTask = (task) => {
    if (!task) {
        return false;
    }
    const seq = task.seq || "";
    const name = task.name || "";
    return seq.endsWith("-00") || name.endsWith(" - ROOT");
};

const getSessionDisplayName = (task) => {
    if (!task) {
        return "";
    }
    const session = task.session || {};
    const sessionName = session.sessionName || session.name;
    if (sessionName) {
        return sessionName;
    }
    const taskName = task.name || "";
    if (taskName.endsWith(" - ROOT")) {
        return taskName.replace(/ - ROOT$/, "");
    }
    return taskName;
};

const getSessionDisplayCode = (task) => {
    if (!task) {
        return "";
    }
    const session = task.session || {};
    return (
        session.sessionCode ||
        session.code ||
        session.seq ||
        task.seq ||
        ""
    );
};

const TaskCard = ({ task, onClick, curTask, doRefresh }) => {
    let { db, apiClient } = useContext(Context);
    const [userDialogOpen, setUserDialogOpen] = useState(false);
    const [users, setUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [loading, setLoading] = useState(false);

    const doSubmit = async () => {
        try {
            // Get the latest task data from backend to check current status
            const latestTask = await apiClient.getTask(task.id);
            
            // Check if task has been processed (has upload data)
            // Tasks stay in "check" status after upload, until 提交 is clicked
            // We need to check if the task has inspection data in the database
            const hasInspectionData = latestTask.data_dir_path || latestTask.oss_base_path || 
                                     ["annotation", "processing", "upload_in_progress"].includes(latestTask.status);
            const isProcessed = hasInspectionData;
            
            if (isProcessed) {
                // Task has been processed - show user selection dialog
                try {
                    // Fetch available users
                    const userList = await apiClient.getUsers();
                    setUsers(userList || []);
                    // Handle both Id and id field names
                    const firstUserId = userList && userList.length > 0 ? (userList[0].Id || userList[0].id) : null;
                    setSelectedUserId(firstUserId);
                    setUserDialogOpen(true);
                } catch (error) {
                    console.error('Failed to fetch users:', error);
                    alert('获取用户列表失败: ' + (error.message || error));
                }
            } else {
                // No upload data found - cannot proceed to annotation
                alert("请先上传巡检数据再提交标注任务");
            }
        } catch (error) {
            console.error('Failed to check task status:', error);
            alert('状态检查失败: ' + (error.message || error));
        }
    };

    const handleCreateAnnotationTask = async () => {
        if (!selectedUserId) {
            alert('请选择一个用户');
            return;
        }

        setLoading(true);
        try {
            // Call the create annotation task API
            await apiClient.createAnnotationTask(task.id, selectedUserId);
            alert('标注任务创建成功！');
            setUserDialogOpen(false);
            doRefresh();
        } catch (error) {
            console.error('Failed to create annotation task:', error);
            alert('创建标注任务失败: ' + (error.message || error));
        } finally {
            setLoading(false);
        }
    };

    let styles = useStyles();
    const rootTask = isRootTask(task);
    const sessionName = getSessionDisplayName(task);
    const sessionCode = getSessionDisplayCode(task);
    const completedAt = task?.completed_at
        ? new Date(task.completed_at).toLocaleDateString()
        : "-";
    return (
        <div
            className={styles.card}
            style={{
                backgroundColor:
                    idString(curTask?.id) == idString(task.id)
                        ? tokens.colorBrandBackground2
                        : "white",
            }}
        >
            <h3>{`任务编号: ${sessionCode}`}</h3>
            <div
                style={{
                    flex: 1,
                    display: "grid",
                    gridTemplateColumns: "1fr 2fr",
                    gap: 10,
                }}
            >
                <Label>任务名称</Label>
                <Label>{sessionName || "未命名任务"}</Label>
                <Label>当前状态</Label>
                <Label>{cvtTaskStatus(task.status)}</Label>
                <Label>任务截止</Label>
                <Label>
                    {completedAt}
                </Label>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
                <Button onClick={onClick} icon={<EyeRegular />}>
                    查看
                </Button>
                <Button icon={<NextFilled />} onClick={doSubmit} disabled={rootTask}>
                    提交
                </Button>
            </div>

            <Dialog open={userDialogOpen} onOpenChange={(event, data) => setUserDialogOpen(data.open)}>
                <DialogSurface>
                    <DialogBody>
                        <DialogTitle>选择标注人员</DialogTitle>
                        <DialogContent>
                            <Field label="请选择要分配标注任务的用户">
                                <Dropdown
                                    value={users.find(u => (u.Id || u.id) === selectedUserId)?.Account || ''}
                                    selectedOptions={[selectedUserId?.toString()]}
                                    onOptionSelect={(e, data) => setSelectedUserId(parseInt(data.optionValue))}
                                    disabled={loading}
                                >
                                    {users.map(user => (
                                        <Option key={user.Id || user.id} value={(user.Id || user.id).toString()}>
                                            {user.Account || user.username} - {user.Name || user.name} ({user.Department || '无部门'})
                                        </Option>
                                    ))}
                                </Dropdown>
                            </Field>
                        </DialogContent>
                        <DialogActions>
                            <DialogTrigger disableButtonEnhancement>
                                <Button appearance="secondary" disabled={loading}>取消</Button>
                            </DialogTrigger>
                            <Button 
                                appearance="primary" 
                                onClick={handleCreateAnnotationTask}
                                disabled={loading || !selectedUserId}
                            >
                                {loading ? '创建中...' : '确认创建'}
                            </Button>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </div>
    );
};

const TaskList = ({ tasks, onSelect, doRefresh, currentTask }) => {
    let [curTask, setCurTask] = useState(null);
    let styles = useStyles();
    useEffect(() => {
        if (!currentTask) {
            setCurTask(null);
            return;
        }
        if (!curTask || idString(curTask.id) !== idString(currentTask.id)) {
            setCurTask(currentTask);
        }
    }, [currentTask, curTask]);
    return (
        <div className={styles.list}>
            <h3
                style={{
                    background: "white",
                    padding: 10,
                    boxShadow: tokens.shadow8,
                }}
            >
                任务列表: 共计 {tasks?.length || 0} 任务
            </h3>
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                    overflowY: "auto",
                }}
            >
                {(tasks || []).map((t, i) => (
                    <TaskCard
                        doRefresh={doRefresh}
                        curTask={curTask}
                        task={t}
                        key={i}
                        onClick={() => onSelect(t)}
                    />
                ))}
            </div>
        </div>
    );
};

const LeafUpload = ({ attachId, turbineId }) => {
    let [leaf, setLeaf] = useState(null);
    let { db, apiClient } = useContext(Context);

    const getLeaf = async () => {
        try {
            // TODO: Implement leaf API endpoint with turbine and attach_id filtering
            const [ret] = await db.query(
                `SELECT * FROM ONLY leaf WHERE turbine=type::thing($turbineId) AND attach_id=$attachId LIMIT 1 ORDER BY installed_at DESC fetch model,model.manu`,
                {
                    turbineId,
                    attachId,
                },
            );
            setLeaf(ret);
        } catch (error) {
            console.error('Failed to load leaf:', error);
            setLeaf(null);
        }
    };

    useEffect(() => {
        if (db) getLeaf();
    }, [db]);

    const labelStyle = {
        backgroundColor: tokens.colorBrandBackground2,
        padding: 5,
        borderRadius: 2,
    };

    let styles = useStyles();

    return leaf ? (
        <div className={styles.leafUpload}>
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr 1fr",
                    padding: 5,
                    gap: 5,
                    alignItems: "center",
                    border: "1px solid #eee",
                }}
            >
                <Label style={labelStyle}>型号</Label>
                <span>{leaf?.model?.seq}</span>
                <Label style={labelStyle}>厂商</Label>
                <span>{leaf?.model?.manu?.name}</span>
            </div>
            <div
                style={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 10,
                    justifyContent: "space-between",
                }}
            >
                <Button size="small">前缘</Button>
                <Button size="small">腹板</Button>
                <Button size="small">后缘</Button>
            </div>
        </div>
    ) : (
        <div
            className={styles.leafUpload}
            style={{
                justifyContent: "center",
                alignItems: "center",
                color: "gray",
                fontSize: 24,
            }}
        >
            无叶片
        </div>
    );
};

const getFileUrl = async (taskId, rawDataId) => {
    try {
        const apiEndpoint = process.env.REACT_APP_API_ENDPOINT || 'http://localhost:5001';
        const response = await fetch(`${apiEndpoint}/api/file/url/rawdata/${taskId}/${rawDataId}`, {
            headers: {
                "Authorization": `Bearer ${localStorage.getItem('token')}`
            }
        });
        const data = await response.json();
        return data.url;
    } catch (error) {
        console.error('Error getting file URL:', error);
        return null;
    }
};

const RawDataDef = new TableDef("raw_data", {
    name: {
        label: "文件名",
    },
    uploaded_by: {
        label: "上传者",
    },
    leaf: {
        label: "叶片",
    },
    part: {
        label: "部位",
    },
    status: {
        label: "状态",
    },
});

const RawDataList = ({ turbineId, task, refresh, setRefresh }) => {
    let { db, oss, apiClient } = useContext(Context);
    let [items, setItems] = useState([]);

    const listItem = async () => {
        try {
            // TODO: Implement raw data API endpoint
            // For now, use placeholder to avoid 404 errors
            const rawData = await apiClient.getRawData(task.id);
            setItems(rawData || []);
        } catch (error) {
            console.error('Failed to load raw data:', error);
            setItems([]);
        }
    };

    const deleteItem = async (item) => {
        await apiClient.deleteRawData(item.id);
        // Delete file via backend API
        const apiEndpoint = process.env.REACT_APP_API_ENDPOINT || 'http://localhost:5001';
        try {
            await fetch(`${apiEndpoint}/api/file/${item.name}`, {
                method: "DELETE",
                headers: {
                    "Authorization": `Bearer ${localStorage.getItem('token')}`
                }
            });
        } catch (error) {
            console.error('Error deleting file:', error);
        }
        setRefresh(refresh + 1);
    };

    const columns = [
        RawDataDef.createTableColumn("name", {
            renderCell: (item) => {
                let name = item.name;
                // let name = item.name.substring(item.name.length - 10);
                // if (item.name.length > 10) {
                //     name = "..." + name;
                // }
                return name;
            },
        }),
        RawDataDef.createTableColumn("uploaded_by", {
            renderCell: (item) => {
                return item.uploadedBy?.name || item.uploaded_by?.name || "未知";
            },
        }),
        RawDataDef.createTableColumn("leaf", {
            renderCell: (item) => {
                return item.leaf?.attachId || item.leaf?.attach_id || "未知";
            },
        }),
        RawDataDef.createTableColumn("part", {
            renderCell: (item) => {
                return cvtLeafPart(item.part);
            },
        }),
        RawDataDef.createTableColumn("status", {
            renderCell: (item) => {
                return cvtRawDataStatus(item.status);
            },
        }),
        RawDataDef.createOpsColumn({
            renderCell: (item) => {
                return (
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "row",
                            gap: 10,
                        }}
                    >
                        <Link
                            onClick={async () => {
                                const url = await getFileUrl(task.id, item.id);
                                if (url) {
                                    window.open(url, '_blank');
                                } else {
                                    alert('无法获取文件下载链接');
                                }
                            }}
                        >
                            <ArrowDownloadFilled />
                        </Link>
                        <Link
                            onClick={async () => {
                                await deleteItem(item);
                            }}
                        >
                            <DeleteRegular />
                        </Link>
                    </div>
                );
            },
        }),
    ];

    useEffect(() => {
        if (apiClient) listItem();
    }, [apiClient, refresh]);

    return (
        <div>
            <SimpleGrid items={items} columns={columns} />
        </div>
    );
};

const TurbineUpload = ({ turbineId, task, onTaskProcessed }) => {
    let { oss, apiClient } = useContext(Context);
    let [turbine, setTurbine] = useState(null);
    let [progress, setProgress] = useState(null);
    let [leafs, setLeafs] = useState([]);
    let [leaf, setLeaf] = useState(null);
    let [part, setPart] = useState(null);
    let [openUpload, setOpenUpload] = useState(false);
    let [refresh, setRefresh] = useState(0);
    let [dataDirectory, setDataDirectory] = useState('');

    const getTurbine = async () => {
        try {
            const turbine = await apiClient.getTurbine(turbineId);
            setTurbine(turbine);
        } catch (error) {
            console.error('Failed to load turbine:', error);
            setTurbine(null);
        }
    };

    const listLeaf = async () => {
        try {
            let leafs = await apiClient.getLeavesByTurbine(turbineId);
            if (!leafs) leafs = [];
            leafs.sort((a, b) => {
                const aAttachId = a.attachId || a.attach_id || '';
                const bAttachId = b.attachId || b.attach_id || '';
                if (aAttachId > bAttachId) {
                    return 1;
                }

                if (aAttachId < bAttachId) {
                    return -1;
                }

                return 0;
            });
            setLeafs(leafs);
        } catch (error) {
            console.error('Failed to load leaves:', error);
            setLeafs([]);
        }
    };

    useEffect(() => {
        if (apiClient) {
            getTurbine();
        }
    }, [apiClient]);

    useEffect(() => {
        if (apiClient && turbine) {
            listLeaf();
        }
    }, [apiClient, turbine]);

    useEffect(() => {
        setRefresh(refresh + 1);
    }, [openUpload]);

    const labelStyle = {
        backgroundColor: tokens.colorBrandBackground,
        padding: 5,
        borderRadius: 2,
        color: "white",
    };

    let styles = useStyles();
    return (
        <div className={styles.turbineUpload}>
            <Button
                appearance="primary"
                icon={<ArrowUploadRegular />}
                onClick={() => {
                    setDataDirectory('');
                    setOpenUpload(true);
                }}
            >
                上传数据
            </Button>
            <Dialog modalType="alert" open={openUpload}>
                {/* <DialogTrigger disableButtonEnhancement> */}
                {/* </DialogTrigger> */}
                <DialogSurface>
                    <DialogBody>
                        <DialogTitle>处理巡检数据</DialogTitle>
                        <DialogContent
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 10,
                            }}
                        >
                            <Label style={labelStyle}>巡检数据目录路径</Label>

                            <Input
                                size="large"
                                value={dataDirectory || ''}
                                onChange={(e) => setDataDirectory(e.target.value)}
                                placeholder="/path/to/inspection/data"
                                contentBefore={<FolderRegular />}
                            />

                            <div style={{ fontSize: 12, color: "#666", marginBottom: 10 }}>
                                请输入包含 pictures 和 logs 子目录的完整路径
                                <br />
                                例如: /home/user/inspection_data/turbine01
                            </div>
                            <Button
                                disabled={!dataDirectory}
                                appearance="primary"
                                onClick={async () => {
                                    if (!dataDirectory) {
                                        alert('请输入数据目录路径');
                                        return;
                                    }

                                    try {
                                        setProgress(0);
                                        
                                        // Call the enhanced uploader process endpoint
                                        const response = await fetch(`http://localhost:5003/api/droneupload/process-task/${task.id}`, {
                                            method: 'POST',
                                            headers: {
                                                'Content-Type': 'application/json'
                                            },
                                            body: JSON.stringify({
                                                dataDirectory: dataDirectory,
                                                taskMetadata: {
                                                    taskId: task.id,
                                                    taskSeq: task.seq,
                                                    taskName: task.name,
                                                    windfarmId: task.field_id,
                                                    sessionId: `inspect_${task.seq}_${new Date().getTime()}`
                                                }
                                            })
                                        });

                                        if (response.ok) {
                                            const result = await response.json();
                                            setProgress(1);
                                            alert(`巡检数据处理完成: ${result.message}`);
                                            setOpenUpload(false);
                                            setDataDirectory('');
                                            setProgress(null);
                                            // Refresh task list to remove processed task
                                            if (onTaskProcessed) {
                                                onTaskProcessed();
                                            }
                                        } else {
                                            const error = await response.json();
                                            throw new Error(error.error || error.message || 'Unknown error');
                                        }
                                    } catch (error) {
                                        console.error('Failed to process inspection data:', error);
                                        alert('处理失败: ' + (error.message || error));
                                        setProgress(null);
                                    }
                                }}
                            >
                                开始处理巡检数据
                            </Button>
                            {progress != null && (
                                <Field
                                    validationMessage={`数据处理中${(
                                        progress * 100
                                    ).toFixed(0)}%, 请勿关闭/刷新页面...`}
                                    validationState="error"
                                >
                                    <ProgressBar value={progress} />
                                </Field>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setOpenUpload(false)}>
                                取消
                            </Button>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
            <h3>已上传数据</h3>
            <RawDataList
                turbineId={turbineId}
                task={task}
                refresh={refresh}
                setRefresh={setRefresh}
            />
        </div>
    );
};

const TaskUpload = ({ task, doRefresh }) => {
    let [openDrawer, setOpenDrawer] = useState(false);
    let [turbineId, setTurbineId] = useState(null);
    let [currentTaskForDrawer, setCurrentTaskForDrawer] = useState(null);
    let [turbineStatuses, setTurbineStatuses] = useState({});
    let { apiClient } = useContext(Context);

    // Initialize turbine statuses when task changes
    useEffect(() => {
        if (task?.turbines) {
            const initialStatuses = {};
            task.turbines.forEach(turbine => {
                // Check if turbine has already been uploaded/processed
                initialStatuses[turbine.id] = {
                    status: 'waiting', // 'waiting', 'uploading', 'uploaded', 'submitting', 'submitted'
                    message: '待上传'
                };
            });
            setTurbineStatuses(initialStatuses);
        }
    }, [task]);

    const updateTurbineStatus = (turbineId, status, message) => {
        setTurbineStatuses(prev => ({
            ...prev,
            [turbineId]: { status, message }
        }));
    };

    const handleTurbineUpload = async (turbine) => {
        try {
            // If task has a sessionId, we need to create/get a task for this specific turbine
            if (task.sessionId) {
                updateTurbineStatus(turbine.id, 'creating', '创建任务中...');

                // Create or get existing task for this turbine in the session
                const taskResult = await apiClient.createSessionTurbineTask(task.sessionId, turbine.id);

                if (taskResult.id) {
                    // Update local task reference to the turbine-specific task
                    const turbineTask = await apiClient.getTask(taskResult.id);

                    // Store the turbine-specific task ID for this turbine
                    if (!window.turbineTaskMap) {
                        window.turbineTaskMap = {};
                    }
                    window.turbineTaskMap[turbine.id] = turbineTask;

                    console.log(`Task ${taskResult.id} (${taskResult.seq}) ready for turbine ${turbine.name}`);
                }
            }

            setTurbineId(turbine.id);
            // Set the current task for the drawer (either session-specific or the original task)
            setCurrentTaskForDrawer(window.turbineTaskMap?.[turbine.id] || task);
            setOpenDrawer(true);
            updateTurbineStatus(turbine.id, 'uploading', '上传中...');
        } catch (error) {
            console.error('Failed to prepare task for upload:', error);
            updateTurbineStatus(turbine.id, 'error', '任务创建失败');
            alert('无法创建任务: ' + (error.message || error));
        }
    };

    const handleTurbineSubmit = async (turbine) => {
        updateTurbineStatus(turbine.id, 'submitting', '提交中...');

        try {
            // Get the turbine-specific task
            let turbineTask = window.turbineTaskMap?.[turbine.id];

            // If no task found in map, try to create/get it
            if (!turbineTask && task.sessionId) {
                const taskResult = await apiClient.createSessionTurbineTask(task.sessionId, turbine.id);
                if (taskResult.id) {
                    turbineTask = await apiClient.getTask(taskResult.id);
                    if (!window.turbineTaskMap) {
                        window.turbineTaskMap = {};
                    }
                    window.turbineTaskMap[turbine.id] = turbineTask;
                }
            }

            if (!turbineTask) {
                throw new Error('No task found for this turbine');
            }

            // Get the latest task data from backend to check current status
            const latestTask = await apiClient.getTask(turbineTask.id);

            // Check if task has been processed for this turbine
            const hasInspectionData = latestTask.data_dir_path || latestTask.oss_base_path ||
                                     ["annotation", "processing", "upload_in_progress", "check"].includes(latestTask.status);

            if (hasInspectionData) {
                // Use the turbine-specific task ID for annotation
                const apiEndpoint = process.env.REACT_APP_API_ENDPOINT || 'http://localhost:5003';
                const response = await fetch(`${apiEndpoint}/api/labelu/create-annotation-task/${turbineTask.id}`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        labelUUserId: 1  // Default user ID, can be made dynamic
                    })
                });

                if (response.ok) {
                    const result = await response.json();
                    updateTurbineStatus(turbine.id, 'submitted', '已提交');
                    alert(`风机 ${turbine.name} 标注任务创建成功！\nLabelU任务ID: ${result.labeluTaskId}`);
                    doRefresh();
                } else {
                    const error = await response.json();
                    if (error.error?.includes('already has LabelU task')) {
                        updateTurbineStatus(turbine.id, 'submitted', '已提交');
                        alert(`风机 ${turbine.name} 已有标注任务`);
                    } else {
                        throw new Error(error.error || error.message);
                    }
                }
            } else {
                updateTurbineStatus(turbine.id, 'uploaded', '✅ 上传完成');
                alert("请先完成上传再提交");
            }
        } catch (error) {
            console.error('Failed to submit turbine:', error);
            updateTurbineStatus(turbine.id, 'uploaded', '✅ 上传完成');
            alert('提交失败: ' + (error.message || error));
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'waiting': return '#666';
            case 'uploading': return '#0078d4';
            case 'uploaded': return '#107c10';
            case 'submitting': return '#0078d4';
            case 'submitted': return '#107c10';
            default: return '#666';
        }
    };

    const columns = [
        createTableColumn({
            columnId: "turbine_seq",
            renderHeaderCell: () => {
                return "风机编号";
            },
            renderCell: (item) => {
                return item.seq;
            },
        }),
        createTableColumn({
            columnId: "turbine_name",
            renderHeaderCell: () => {
                return "风机名称";
            },
            renderCell: (item) => {
                return item.name;
            },
        }),
        createTableColumn({
            columnId: "field",
            renderHeaderCell: () => {
                return "所属风场";
            },
            renderCell: (item) => {
                return item?.field_item?.name;
            },
        }),
        createTableColumn({
            columnId: "turbine_model",
            renderHeaderCell: () => {
                return "型号";
            },
            renderCell: (item) => {
                return item?.model?.seq;
            },
        }),
        createTableColumn({
            columnId: "turbine_manu",
            renderHeaderCell: () => {
                return "厂商";
            },
            renderCell: (item) => {
                return item?.model?.manu?.name;
            },
        }),
        createTableColumn({
            columnId: "status",
            renderHeaderCell: () => {
                return "状态";
            },
            renderCell: (item) => {
                const turbineStatus = turbineStatuses[item.id] || { status: 'waiting', message: '待上传' };
                return (
                    <span style={{
                        color: getStatusColor(turbineStatus.status),
                        fontWeight: turbineStatus.status === 'submitted' ? 'bold' : 'normal'
                    }}>
                        {turbineStatus.message}
                    </span>
                );
            },
        }),
        createTableColumn({
            columnId: "actions",
            renderHeaderCell: () => {
                return "操作";
            },
            renderCell: (item) => {
                const alreadyHasTask = item.has_task === true || item.hasTask === true;
                const turbineStatus = turbineStatuses[item.id] || { status: alreadyHasTask ? 'submitted' : 'waiting' };
                const isUploaded = turbineStatus.status === 'uploaded';
                const isSubmitted = turbineStatus.status === 'submitted';
                const isProcessing = turbineStatus.status === 'uploading' || turbineStatus.status === 'submitting';

                return (
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <Button
                            appearance="primary"
                            icon={<ArrowUploadRegular />}
                            size="small"
                            disabled={isSubmitted || isProcessing || alreadyHasTask}
                            onClick={() => handleTurbineUpload(item)}
                        >
                            上传
                        </Button>
                        <Button
                            appearance={isUploaded ? "primary" : "secondary"}
                            icon={<NextFilled />}
                            size="small"
                            disabled={!isUploaded || isSubmitted || isProcessing || alreadyHasTask}
                            onClick={() => handleTurbineSubmit(item)}
                        >
                            提交
                        </Button>
                    </div>
                );
            },
        }),
    ];

    let styles = useStyles();

    return (
        <div className={styles.upload}>
            <DrawerOverlay
                style={{ width: "800px" }}
                position="right"
                open={openDrawer}
                onOpenChange={(_, { open }) => setOpenDrawer(open)}
            >
                <DrawerHeader>
                    <DrawerHeaderTitle
                        action={
                            <Button
                                appearance="subtle"
                                aria-label="Close"
                                icon={<Dismiss24Regular />}
                                onClick={() => setOpenDrawer(false)}
                            />
                        }
                    >
                        上传数据
                    </DrawerHeaderTitle>
                </DrawerHeader>
                <DrawerBody>
                    <TurbineUpload
                        turbineId={turbineId}
                        task={currentTaskForDrawer || task}
                        onTaskProcessed={() => {
                            // Update status to uploaded
                            if (turbineId) {
                                updateTurbineStatus(turbineId, 'uploaded', '✅ 上传完成');
                            }
                            // Close drawer and refresh task list
                            setOpenDrawer(false);
                            doRefresh();
                        }}
                    />
                </DrawerBody>
            </DrawerOverlay>
            <h3>上传任务数据: 共计 {task?.turbines.length || 0} 风机</h3>
            <div
                style={{
                    flex: 1,
                    overflowY: "auto",
                }}
            >
                <DataGrid columns={columns} items={task?.turbines || []}>
                    <DataGridHeader>
                        <DataGridRow>
                            {({ renderHeaderCell }, i) => (
                                <DataGridHeaderCell key={i}>
                                    {renderHeaderCell()}
                                </DataGridHeaderCell>
                            )}
                        </DataGridRow>
                    </DataGridHeader>
                    <DataGridBody>
                        {({ item }) => (
                            <DataGridRow key={item.seq}>
                                {({ renderCell }) => (
                                    <DataGridCell>
                                        {renderCell(item)}
                                    </DataGridCell>
                                )}
                            </DataGridRow>
                        )}
                    </DataGridBody>
                </DataGrid>
                {/* {(task?.turbines || []).map((turbineId) => {
                    return <TurbineUpload turbineId={turbineId} />;
                })} */}
            </div>
        </div>
    );
};

export const TaskCheck = () => {
    let { apiClient, currentField } = useContext(Context);
    let user = getCurrentUser();
    let [tasks, setTasks] = useState(null);
    let [task, setTask] = useState(null);
    let [refresh, setRefresh] = useState(0);

    const doRefresh = () => {
        setRefresh(refresh + 1);
    };

    const listTask = async () => {
        try {
            const params = {
                status: 'check'
            };
            if (user.role?.toLowerCase() !== "admin" && user.role?.toLowerCase() !== "field_admin") {
                params.checkerId = user.id;
            }
            // Get current field from Context
            if (currentField && currentField.id) {
                params.fieldId = currentField.id;
            }
            const response = await apiClient.getTasks(params);
            const realTasks = response || [];
            setTasks(realTasks);
            setTask((prev) => {
                if (!prev) {
                    return realTasks[0] || null;
                }
                const exists = realTasks.some(
                    (t) => idString(t.id) === idString(prev.id),
                );
                return exists ? prev : realTasks[0] || null;
            });
        } catch (error) {
            console.error('Failed to load check tasks:', error);
            setTasks([]);
        }
    };

    useEffect(() => {
        if (apiClient) listTask();
    }, [apiClient, refresh, currentField]);

    let styles = useStyles();
    return (
        <div className={styles.root}>
            <TaskList
                tasks={tasks}
                onSelect={(task) => {
                    setTask(task);
                }}
                doRefresh={doRefresh}
                currentTask={task}
            />
            <TaskUpload task={task} doRefresh={doRefresh} />
        </div>
    );
};
